import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  BOUNDARY_OPTIONS,
  CONDITION_OPERATOR_OPTIONS,
  LIMITS_ROUTE,
  REQUIREMENT_OPTIONS,
  applyLimitDerivedFields,
  applyLimitSelectionChange,
  buildLimitScopeKey,
  buildSampleLimitRules,
  createEmptyCondition,
  createEmptyLimitForm,
  formatConditionsPreview,
  formatLimitDateTime,
  formatLimitDisplay,
  getAvailableConditionFields,
  getCompatibleLimitTypes,
  getCompatibleOperators,
  getCompatibleUnits,
  getConditionFieldLabel,
  getConditionValueLabel,
  getConditionValueOptions,
  getLimitForDefinition,
  getLimitForLabel,
  getLimitForOptions,
  getLimitTypeLabel,
  getMeasureLabel,
  getOperatorLabel,
  getPurposeMeaning,
  getPurposeReference,
  getRequirementLabel,
  getUnitLabel,
  normalizeLimitForm,
  operatorIsMulti,
} from '../../../data/limitsThresholdsConfig';
import {
  canAddLimitRule,
  canEditLimitRule,
  canSaveLimitRule,
  canViewLimitsConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveLimitRule,
} from '../../../data/limitRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyLimitForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeLimitForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const MultiChecklist = ({ label, options, values, onChange, disabled, error, required = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2 border border-lightgray rounded-xl px-3 py-3 max-h-56 overflow-y-auto">
      {disabled || !options.length ? (
        <AdminSupportingText className="text-mediumgray">
          {disabled ? 'Complete the previous selections first.' : 'No compatible values available.'}
        </AdminSupportingText>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 text-sm text-offblack">
            <input
              type="checkbox"
              className="accent-primaryblue mt-0.5 shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="break-words">{option.label}</span>
          </label>
        ))
      )}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: `${getLabel(value) || value} (Unavailable)` }];
};

const withCurrentOptions = (options, values, getLabel) => {
  let next = options;
  (values || []).forEach((value) => {
    next = withCurrentOption(next, value, getLabel);
  });
  return next;
};

const LimitRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewLimitsConfiguration(auth);
  const canAdd = canAddLimitRule(auth);
  const canEdit = canEditLimitRule(auth);
  const canSave = canSaveLimitRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleLimitRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  const scopeLocked = Boolean(!isCreate && formData.id);
  const purpose = getLimitForDefinition(formData.limitFor);
  const limitForOptions = useMemo(
    () => withCurrentOption(getLimitForOptions(formData.requirement), formData.limitFor, getLimitForLabel),
    [formData.requirement, formData.limitFor],
  );
  const limitTypeOptions = useMemo(
    () => withCurrentOption(getCompatibleLimitTypes(formData.limitFor), formData.limitType, getLimitTypeLabel),
    [formData.limitFor, formData.limitType],
  );
  const operatorOptions = useMemo(
    () => withCurrentOption(getCompatibleOperators(formData.limitFor, formData.limitType), formData.operator, getOperatorLabel),
    [formData.limitFor, formData.limitType, formData.operator],
  );
  const unitOptions = useMemo(
    () => withCurrentOption(getCompatibleUnits(formData.limitFor), formData.unit, getUnitLabel),
    [formData.limitFor, formData.unit],
  );
  const conditionFields = useMemo(
    () => getAvailableConditionFields(formData.limitFor),
    [formData.limitFor],
  );
  const canAddCondition = conditionFields.length > 0;
  const limitTypeLocked = limitTypeOptions.length === 1 || Boolean(purpose?.limitTypes?.length === 1);
  const operatorLocked = operatorOptions.length === 1;
  const unitLocked = unitOptions.length === 1 || Boolean(purpose?.fixedUnit);
  const valueLocked = purpose?.fixedValue != null;
  const showValue = formData.limitType === 'single_value';
  const showRange = formData.limitType === 'min_max_range';

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const handleChange = (field, value) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        activation: undefined,
        duplicate: undefined,
        conflict: undefined,
        overlap: undefined,
        concurrent: undefined,
        scope: undefined,
        form: undefined,
        permission: undefined,
      };
      if (field === 'conditions') {
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('condition_')) delete nextErr[key];
        });
      }
      return nextErr;
    });
    setFormData((prev) => {
      if (['requirement', 'limitFor', 'limitType', 'unit'].includes(field)) {
        return applyLimitSelectionChange(prev, field, value);
      }
      if (field === 'operator') return applyLimitDerivedFields({ ...prev, operator: value });
      return { ...prev, [field]: value };
    });
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, conditions: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) conditions[index].values = [];
      if (patch.operator && !operatorIsMulti(patch.operator)) {
        conditions[index].values = (conditions[index].values || []).slice(0, 1);
      }
      return { ...prev, conditions };
    });
  };

  const handleSave = () => {
    if (isCreate && !canAdd) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    if (isEdit && !canEdit) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    const result = saveLimitRule(allRules, formData, publishedBaseline || {}, {
      originalLastUpdated: resolvedSource.lastUpdated,
      originalScopeKey: publishedBaseline ? buildLimitScopeKey(publishedBaseline) : undefined,
      canSave,
    });
    if (!result.success) {
      setErrors({ ...result.errors, activation: result.summary.join(' ') });
      return;
    }
    navigate(LIMITS_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true, emptyMessage = 'No compatible options available') => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`lth-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? emptyMessage : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const renderNumber = (name, label, locked = false) => {
    if (isView || locked) {
      return isView
        ? <TextValue label={label} value={formData[name] === '' || formData[name] == null ? '—' : String(formData[name])} />
        : <AdminConfigLockedField label={label} value={formData[name] === '' || formData[name] == null ? '—' : String(formData[name])} isView={false} />;
    }
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} <span className="text-alertred">*</span>
        </AdminDetailFieldLabel>
        <input
          id={`lth-${name}`}
          type="text"
          inputMode="decimal"
          placeholder={`Enter ${label.toLowerCase()}`}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          className={adminFormDetailInputClasses(false, Boolean(errors[name]))}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const conditionsPreview = formatConditionsPreview(formData.conditions);

  if (!canView && isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Limits & Thresholds Rule Detail</AdminPageTitle>
        <AdminFormFieldError>You do not have permission to view this Rule.</AdminFormFieldError>
      </AdminLayout>
    );
  }

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Limits & Thresholds Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <div className="sm:col-span-2">
                <TextValue label="Description" value={formData.description} />
              </div>
              <TextValue label="Requirement" value={getRequirementLabel(formData.requirement)} />
              <TextValue label="Limit For" value={getLimitForLabel(formData.limitFor)} />
              <div className="sm:col-span-2">
                <TextValue label="Purpose meaning" value={getPurposeMeaning(formData.limitFor) || '—'} />
              </div>
              <TextValue label="Purpose reference" value={getPurposeReference(formData.limitFor) || '—'} mono />
              <TextValue label="Measure" value={getMeasureLabel(formData.requirement) || '—'} />
              <TextValue label="Limit Type" value={getLimitTypeLabel(formData.limitType)} />
              <TextValue label="Operator" value={getOperatorLabel(formData.operator)} />
              <TextValue label="Unit" value={getUnitLabel(formData.unit)} />
              {showValue ? (
                <TextValue label="Value" value={formData.value === '' ? '—' : String(formData.value)} />
              ) : null}
              {showRange ? (
                <>
                  <TextValue label="Minimum" value={formData.minimum === '' ? '—' : String(formData.minimum)} />
                  <TextValue label="Maximum" value={formData.maximum === '' ? '—' : String(formData.maximum)} />
                  <TextValue
                    label="Minimum Boundary"
                    value={BOUNDARY_OPTIONS.find((option) => option.value === formData.minimumBoundary)?.label || '—'}
                  />
                  <TextValue
                    label="Maximum Boundary"
                    value={BOUNDARY_OPTIONS.find((option) => option.value === formData.maximumBoundary)?.label || '—'}
                  />
                </>
              ) : null}
              <div className="sm:col-span-2">
                <TextValue label="Limit" value={formatLimitDisplay(formData)} />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : 'No Conditions'}
                />
              </div>
              <TextValue label="Date Added" value={formatLimitDateTime(formData.dateAdded)} />
              <TextValue label="Added By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatLimitDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  const hasValidationErrors = Boolean(
    errors.permission
    || errors.checksUnavailable
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.concurrent
    || errors.scope
    || errors.form
    || errors.activation
    || errors.conditions,
  );

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Limit Rule' : 'Edit Limit Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="lth-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
        </div>
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Description <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <textarea
            id="lth-description"
            placeholder="Enter a description"
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            className={adminFormDetailTextareaClasses(false, Boolean(errors.description))}
            rows={3}
          />
          {errors.description ? <AdminFormFieldError>{errors.description}</AdminFormFieldError> : null}
        </div>

        {scopeLocked ? (
          <>
            <AdminConfigLockedField label="Requirement" value={getRequirementLabel(formData.requirement)} isView={false} />
            <AdminConfigLockedField label="Limit For" value={getLimitForLabel(formData.limitFor)} isView={false} />
          </>
        ) : (
          <>
            {renderSelect('requirement', 'Requirement', REQUIREMENT_OPTIONS, 'Select requirement')}
            {renderSelect(
              'limitFor',
              'Limit For',
              limitForOptions,
              formData.requirement ? 'Select Limit For' : 'Select requirement first',
              !formData.requirement,
              true,
              'No compatible purposes available.',
            )}
          </>
        )}
        <AdminConfigLockedField
          label="Purpose meaning"
          value={getPurposeMeaning(formData.limitFor) || '—'}
          isView={false}
        />
        <AdminConfigLockedField
          label="Purpose reference"
          value={getPurposeReference(formData.limitFor) || '—'}
          mono
          isView={false}
        />
        <AdminConfigLockedField
          label="Measure"
          value={getMeasureLabel(formData.requirement) || '—'}
          isView={false}
        />
        {limitTypeLocked ? (
          <AdminConfigLockedField label="Limit Type" value={getLimitTypeLabel(formData.limitType) || '—'} isView={false} />
        ) : (
          renderSelect(
            'limitType',
            'Limit Type',
            limitTypeOptions,
            formData.limitFor ? 'Select Limit Type' : 'Select Limit For first',
            !formData.limitFor,
          )
        )}
        {operatorLocked ? (
          <AdminConfigLockedField label="Operator" value={getOperatorLabel(formData.operator) || '—'} isView={false} />
        ) : (
          renderSelect(
            'operator',
            'Operator',
            operatorOptions,
            formData.limitType ? 'Select operator' : 'Select Limit Type first',
            !formData.limitType,
          )
        )}
        {unitLocked ? (
          <AdminConfigLockedField label="Unit" value={getUnitLabel(formData.unit) || '—'} isView={false} />
        ) : (
          renderSelect(
            'unit',
            'Unit',
            unitOptions,
            formData.limitFor ? 'Select unit' : 'Select Limit For first',
            !formData.limitFor,
          )
        )}
        {showValue ? renderNumber('value', 'Value', valueLocked) : null}
        {showRange ? (
          <>
            {renderNumber('minimum', 'Minimum')}
            {renderNumber('maximum', 'Maximum')}
            {renderSelect('minimumBoundary', 'Minimum Boundary', BOUNDARY_OPTIONS, 'Select minimum boundary')}
            {renderSelect('maximumBoundary', 'Maximum Boundary', BOUNDARY_OPTIONS, 'Select maximum boundary')}
          </>
        ) : null}

        <div className="space-y-3 w-full min-w-0">
          <AdminDetailFieldLabel>Optional Conditions</AdminDetailFieldLabel>
          {(formData.conditions || []).length ? (
            <div className="space-y-3">
              {(formData.conditions || []).map((condition, index) => {
                const valueOptions = withCurrentOptions(
                  getConditionValueOptions(condition.field, formData.conditions),
                  condition.values,
                  (value) => getConditionValueLabel(condition.field, value),
                );
                const multi = operatorIsMulti(condition.operator);
                return (
                  <div key={condition.id || index} className="rounded-xl border border-lightgray p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <AdminSupportingText className="mb-0 font-semibold !text-offblack">Condition</AdminSupportingText>
                      <button
                        type="button"
                        onClick={() => handleChange(
                          'conditions',
                          (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index),
                        )}
                        className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                        aria-label="Remove Condition"
                      >
                        <i className="fi fi-rs-trash text-sm" aria-hidden />
                      </button>
                    </div>
                    {errors[`condition_${index}`] ? (
                      <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError>
                    ) : null}
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.field || ''}
                          onChange={(e) => updateCondition(index, { field: e.target.value })}
                          className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                        >
                          <option value="">Select field</option>
                          {withCurrentOption(conditionFields, condition.field, getConditionFieldLabel).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.operator || ''}
                          onChange={(e) => updateCondition(index, { operator: e.target.value })}
                          disabled={!condition.field}
                          className={adminFormDetailSelectClasses(!condition.field)}
                        >
                          <option value="">Select operator</option>
                          {CONDITION_OPERATOR_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    {multi ? (
                      <MultiChecklist
                        label="Value"
                        options={getConditionValueOptions(condition.field, formData.conditions)}
                        values={condition.values}
                        onChange={(value) => updateCondition(index, { values: value })}
                        disabled={!condition.field}
                        required
                      />
                    ) : (
                      <div className="space-y-1 w-full min-w-0">
                        <AdminDetailFieldLabel>Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                        <div className="relative w-full min-w-0">
                          <select
                            value={(condition.values || [])[0] || ''}
                            onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                            disabled={!condition.field || !valueOptions.length}
                            className={adminFormDetailSelectClasses(!condition.field || !valueOptions.length)}
                          >
                            <option value="">
                              {!condition.field
                                ? 'Select field first'
                                : valueOptions.length
                                  ? 'Select value'
                                  : 'No compatible values available'}
                            </option>
                            {valueOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <AdminFormDropdownChevron />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <AdminDetailFieldValue className="py-2">
              {canAddCondition ? 'No Conditions added' : 'No compatible Conditions available'}
            </AdminDetailFieldValue>
          )}
          {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          {canAddCondition ? (
            <button
              type="button"
              onClick={() => handleChange('conditions', [...(formData.conditions || []), createEmptyCondition()])}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
          ) : null}
        </div>

        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Change Reason <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <textarea
            id="lth-change-reason"
            placeholder="Enter a change reason"
            value={formData.changeReason || ''}
            onChange={(e) => handleChange('changeReason', e.target.value)}
            className={adminFormDetailTextareaClasses(false, Boolean(errors.changeReason))}
            rows={3}
          />
          {errors.changeReason ? <AdminFormFieldError>{errors.changeReason}</AdminFormFieldError> : null}
        </div>

        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.scope ? <AdminFormFieldError>{errors.scope}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          </div>
        ) : null}

        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default LimitRuleDetail;
